import React, { useState } from "react";
import styled from "styled-components";
import Button from "@material-ui/core/Button";
import PersonIcon from "@material-ui/icons/Person";
import StarIcon from "@material-ui/icons/Star";
import { useAuth } from "../contexts/AuthContext";
import { Link, useHistory } from "react-router-dom";
import logo from "../images/logo.svg";

const Nav = styled.nav`
  width: 100%;
  height: 64px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  padding: 0 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    height: 56px;
  }
`;

const NavContent = styled.div`
  display: flex;
  justify-content: ${({ isLoggedIn }) => (isLoggedIn ? "flex-end" : "space-between")};
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const StyledBurger = styled.div`
  width: 38px;
  height: 38px;
  position: relative;
  display: none;
  cursor: pointer;
  border-radius: 10px;
  padding: 7px;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(0, 120, 212, 0.08);
  }
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
  }
  
  div {
    width: 24px;
    height: 2px;
    background-color: ${({ open }) => (open ? "#0078D4" : "#111827")};
    border-radius: 10px;
    transform-origin: 1px;
    transition: all 0.3s linear;
    
    &:nth-child(1) {
      transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0)")};
    }
    
    &:nth-child(2) {
      opacity: ${({ open }) => (open ? 0 : 1)};
      transform: ${({ open }) => (open ? "translateX(20px)" : "translateX(0)")};
    }
    
    &:nth-child(3) {
      transform: ${({ open }) => (open ? "rotate(-45deg)" : "rotate(0)")};
    }
  }
`;

const Ul = styled.ul`
  display: flex;
  align-items: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: 768px) {
    display: ${({ open }) => (open ? "flex" : "none")};
    flex-direction: column;
    position: fixed;
    top: 56px;
    right: 0;
    width: 100%;
    height: calc(100vh - 56px);
    background: rgba(255, 255, 255, 0.98);
    padding: 24px;
    gap: 10px;
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.12);
    transition: transform 0.3s ease-in-out;
    transform: ${({ open }) => (open ? "translateX(0)" : "translateX(100%)")};
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #334155;
  font-weight: 650;
  font-size: 13px;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 38px;
  padding: 8px 13px;
  border-radius: 999px;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    color: #005a9e;
    background-color: rgba(0, 120, 212, 0.08);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(0, 120, 212, 0.35);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    min-height: 46px;
    padding: 12px 18px;
    width: 100%;
    justify-content: center;
    border-radius: 12px;
  }
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  border-radius: 10px;
  padding: 4px;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(0, 120, 212, 0.08);
    transform: translateY(-1px);
  }
  
  img {
    height: 32px;
    width: 32px;
    border-radius: 6px;
  }
`;

const NavButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #0078D4, #006cbe);
    color: white;
    font-weight: 700;
    padding: 8px 18px;
    border-radius: 999px;
    text-transform: none;
    font-size: 13px;
    letter-spacing: 0;
    box-shadow: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
    
    &:hover {
      background: linear-gradient(135deg, #006cbe, #005a9e);
      box-shadow: 0 8px 18px rgba(0, 120, 212, 0.22);
      transform: translateY(-1px);
    }

    @media (max-width: 768px) {
      width: 100%;
      padding: 12px 20px;
      font-size: 15px;
      border-radius: 12px;
    }
  }
`;

const Navbars = () => {
  const [open, setOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const [error, setError] = useState("");
  const history = useHistory();

  async function handleLogout() {
    setError(error);
    try {
      await logout();
      history.push("/login");
    } catch {
      setError("Failed to log out");
    }
  }
  
  return (
    <Nav>
      <NavContent isLoggedIn={Boolean(currentUser)}>
        {!currentUser && (
          <LogoLink to="/" onClick={() => setOpen(false)} aria-label="Rundoit home">
            <img src={logo} alt="Run Do It Logo" />
          </LogoLink>
        )}
        
        <StyledBurger open={open} onClick={() => setOpen(!open)}>
          <div />
          <div />
          <div />
        </StyledBurger>

        <Ul open={open}>
          {currentUser ? (
            <>
              <li>
                <StyledLink to="/user" onClick={() => setOpen(false)}>
                  <PersonIcon />
                  Dashboard
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/profile" onClick={() => setOpen(false)}>
                  Profile
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/calendar" onClick={() => setOpen(false)}>
                  Calendar
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/pricing" onClick={() => setOpen(false)}>
                  <StarIcon />
                  Upgrade
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/contact" onClick={() => setOpen(false)}>
                  Contact
                </StyledLink>
              </li>
              <li>
                <StyledLink to="#" onClick={handleLogout}>
                  <NavButton variant="contained">
                    Log Out
                  </NavButton>
                </StyledLink>
              </li>
            </>
          ) : (
            <>
              <li>
                <StyledLink to="/demo" onClick={() => setOpen(false)}>
                  Try Demo
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/features" onClick={() => setOpen(false)}>
                  Features
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/pricing" onClick={() => setOpen(false)}>
                  Pricing
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/contact" onClick={() => setOpen(false)}>
                  Contact
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/login" onClick={() => setOpen(false)}>
                  Sign In
                </StyledLink>
              </li>
              <li>
                <StyledLink to="/signup" onClick={() => setOpen(false)}>
                  <NavButton variant="contained">
                    Sign Up
                  </NavButton>
                </StyledLink>
              </li>
            </>
          )}
        </Ul>
      </NavContent>
    </Nav>
  );
};

export default Navbars;
